import { forEach } from 'lodash'
import {
  differenceInDays,
  differenceInHours,
  differenceInMinutes,
  format,
  isThisYear,
  isToday,
  isTomorrow,
  isYesterday,
} from 'date-fns'
import { Maybe } from '@vendorflow/types'
import { EnhancedColumn } from '@vendorflow/components'
import { STANDARD_UUID_REGEX } from 'constants/Regex.constants'

export function sleep(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

export function prettyTimeSinceFormat(datetime: Maybe<number | string>) {
  if (!datetime) {
    return '-'
  }

  const now = new Date()
  const timestamp = new Date(datetime)

  const days = differenceInDays(now, timestamp)
  if (days > 0) {
    return `${days} day` + (days > 1 ? 's' : '')
  }

  const hours = differenceInHours(now, timestamp)
  if (hours > 0) {
    return `${hours} hour` + (hours > 1 ? 's' : '')
  }

  const minutes = differenceInMinutes(now, timestamp)
  return `${minutes} min` + (minutes > 1 ? 's' : '')
}

export function prettyDateFormat(datetime: Maybe<number | string>) {
  const now = new Date()
  const timestamp = datetime ? new Date(datetime) : null

  if (!timestamp) {
    return ''
  }

  if (isToday(timestamp)) {
    const hours = differenceInHours(now, timestamp)
    if (hours > 0) {
      return `${hours} hour${hours === 1 ? '' : 's'} ago`
    }

    const minutes = differenceInMinutes(now, timestamp)
    if (minutes > 0) {
      return `${minutes} minute${minutes === 1 ? '' : 's'} ago`
    }

    return `now`
  }

  return format(timestamp, 'H:mm MM/dd/yyyy')
}

export function prettyMilitaryTimeFormat(datetime: Maybe<number | string | Date>): string {
  if (!datetime) {
    return ''
  }

  const timestamp = datetime instanceof Date ? datetime : new Date(datetime)
  const recentDay = isToday(timestamp)
    ? 'Today'
    : isTomorrow(timestamp)
    ? 'Tomorrow'
    : isYesterday(timestamp)
    ? 'Yesterday'
    : null

  if (recentDay) {
    return recentDay + ' ' + format(timestamp, 'HH:mm')
  } else if (isThisYear(timestamp)) {
    return format(timestamp, 'MM/dd HH:mm')
  } else {
    return format(timestamp, 'MM/dd/yyyy HH:mm')
  }
}

export function truncatedDateFormat(datetime: Maybe<number | string>) {
  const timestamp = datetime != null ? new Date(datetime) : null

  if (!timestamp) {
    return ''
  }

  if (isToday(timestamp)) {
    return format(timestamp, 'h:mm aa')
  }

  if (isThisYear(timestamp)) {
    return format(timestamp, 'MM/dd h:mm aa')
  }

  return format(timestamp, 'MM/dd/yy h:mm aa')
}

export function toHumanReadableHMS(seconds: number): string {
  const hours = Math.floor(seconds / 3600)
  const mins = Math.floor((seconds % 3600) / 60)
  const secs = seconds % 60

  return [hours ? `${hours}h` : '', mins ? `${mins}m` : '', secs ? `${secs}s` : ''].filter(Boolean).join(' ')
}

export function generateSignature(phone: string | null, displayName: string | null) {
  const formattedPhone = phone ? simpleFormatPhoneNumber(phone) : ''
  const formattedDisplayName = displayName ? `—${displayName}` : ''
  const separator = phone && displayName ? ' ' : ''
  return `${formattedPhone}${separator}${formattedDisplayName}`
}

export function simpleFormatPhoneNumber(phone: string) {
  phone = sanitizePhoneNumber(phone)
  const beginning = phone.slice(0, 3)
  const middle = phone.slice(3, 6)
  const end = phone.substring(6)
  return `${beginning}-${middle}-${end}`
}

export function formatPhoneNumber(phone: string): string {
  phone = sanitizePhoneNumber(phone)
  const beginning = phone.slice(0, 3)
  const middle = phone.slice(3, 6)
  const end = phone.substring(6)
  return `(${beginning}) ${middle}-${end}`
}

export function sanitizePhoneNumber(phone: string): string {
  phone = removeNonNumbers(phone)
  return removeFirstOneDigit(phone)
}

export function removeNonNumbers(str: string): string {
  return str.replace(/[^0-9]/g, '')
}

// US phone number area codes don't allow starting with 1
// Removes the US country code of 1
export function removeFirstOneDigit(str: string): string {
  if (str && str.charAt(0) === '1') {
    return str.substr(1)
  }
  return str
}

export function getUpdatedProperties(prev: any, curr: any): any {
  const result: { [key: string]: any } = {}
  forEach(prev, (value: any, property: string) => {
    if (prev[property] !== curr[property]) {
      result[property] = curr[property]
    }
  })
  return result
}

// Used to properly test RegExp -- especially ones w/ global set & we want to
// check if the entire string is valid
export function testRegex(regex: RegExp, value: string, fullyValid?: boolean): { isValid: boolean; matches: string[] } {
  let isValid = false
  let matches: string[] = []
  if (regex.global && fullyValid) {
    const result = Array.from(value.matchAll(regex))
    if (!result.length) {
      return {
        isValid,
        matches,
      }
    }

    const lastMatch = result[result.length - 1]
    isValid = lastMatch.index !== undefined && lastMatch['index'] + lastMatch[0].length === value.length
    matches = result.map((item) => item[0])
    return { isValid, matches }
  }

  regex.lastIndex = 0
  isValid = regex.test(value)
  return {
    isValid,
    matches: isValid ? value.match(regex)! : [],
  }
}

// TODO: use navigator.userAgentData.platform since navigator.platform is deprecated
export const isIOS =
  /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)

// @ts-ignore
export const isMac = /mac/i.test(navigator.userAgentData ? navigator.userAgentData.platform : navigator.platform)

export function getTotalWidthFromColumns(cols: EnhancedColumn<any>[]): number {
  return cols.reduce<number>((acc, col) => {
    const width: number =
      typeof col.width === 'string' && !isNaN(parseInt(col.width))
        ? parseInt(col.width)
        : typeof col.width === 'number'
        ? col.width
        : 150
    return acc + width
  }, 0)
}

export function formatForTestId(value: string): string {
  return value.toLowerCase().replace(/\s/g, '-')
}

export function handleAttachmentRefFormat(ref: string) {
  // to prevent ref string issues when storing in RTDB
  if (ref.indexOf('/') !== -1) {
    return encodeURIComponent(ref).replace(/\./g, '%2E')
  } else {
    return ref
  }
}

export function isUUID(string: string) {
  return STANDARD_UUID_REGEX.test(string)
}

export function getStringWithPadding(value: string | number, paddingOptions: { length: number; symbol: string }) {
  return String(typeof value === 'number' ? String(value) : value).padStart(
    paddingOptions.length,
    paddingOptions.symbol
  )
}

export function setSelectedItemIndexIds<T>(value: string, fromId: string, items: T[], getId: (item: T) => string) {
  const toId = value
  const fromIndex = items.findIndex((item) => getId(item) === fromId)
  const toIndex = items.findIndex((item) => getId(item) === toId)
  const shiftSelectedItems =
    toIndex > fromIndex ? items.slice(fromIndex, toIndex + 1) : items.slice(toIndex, fromIndex + 1)
  return shiftSelectedItems.map((item) => getId(item))
}

export function getDescriptionForList<T>(entities: T[], getName?: (entity: T) => string): string {
  return entities.reduce<string>((acc, entity, index) => {
    const conjunction =
      index === 0 ? '' : entities.length === 2 ? ' and ' : index === entities.length - 1 ? ', and ' : ', '
    acc += `${conjunction}${getName ? getName(entity) : entity}`
    return acc
  }, '')
}
